import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Star, FileText, Trash2, Copy, Edit, Eye, Settings } from 'lucide-react';
import { MainLayout } from '@/components/MainLayout';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCustomReports, useDeleteCustomReport, useDuplicateCustomReport, useToggleFavoriteReport } from '@/hooks/useCustomReports';
import { toast } from 'sonner';

export function CustomReportList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: reports, isLoading } = useCustomReports({ search, favoritesOnly });
  const deleteReport = useDeleteCustomReport();
  const duplicateReport = useDuplicateCustomReport();
  const toggleFavorite = useToggleFavoriteReport();

  const handleDelete = () => {
    if (deleteId) {
      deleteReport.mutate(deleteId, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  const handleDuplicate = (id: string) => {
    duplicateReport.mutate(id);
  };

  const handleToggleFavorite = (id: string) => {
    toggleFavorite.mutate(id);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <SEO
          title="Relatórios Personalizados"
          description="Crie e gerencie seus próprios relatórios financeiros"
        />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Carregando relatórios...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEO
        title="Relatórios Personalizados"
        description="Crie e gerencie seus próprios relatórios financeiros"
      />
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Relatórios Personalizados</h1>
          <p className="text-muted-foreground">
            Crie e gerencie seus próprios relatórios financeiros
          </p>
        </div>
        <Button onClick={() => navigate('/reports/custom/new')} size="lg">
          <Plus className="mr-2 h-5 w-5" />
          Novo Relatório
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar relatórios..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant={favoritesOnly ? 'default' : 'outline'}
          onClick={() => setFavoritesOnly(!favoritesOnly)}
          className="shrink-0"
        >
          <Star className={`mr-2 h-4 w-4 ${favoritesOnly ? 'fill-current' : ''}`} />
          {favoritesOnly ? 'Todos' : 'Favoritos'}
        </Button>
      </div>

      {/* Reports Grid */}
      {!reports || reports.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {search || favoritesOnly ? 'Nenhum relatório encontrado' : 'Nenhum relatório criado'}
            </h3>
            <p className="text-muted-foreground mb-6 text-center max-w-md">
              {search || favoritesOnly
                ? 'Tente ajustar os filtros ou criar um novo relatório.'
                : 'Comece criando seu primeiro relatório personalizado para visualizar seus dados financeiros.'}
            </p>
            <Button onClick={() => navigate('/reports/custom/new')}>
              <Plus className="mr-2 h-4 w-4" />
              Criar Relatório
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <Card
              key={report.id}
              className="group hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/reports/custom/${report.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="truncate flex items-center gap-2">
                      {report.name}
                      {report.is_favorite && (
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 shrink-0" />
                      )}
                    </CardTitle>
                    <CardDescription className="line-clamp-2 mt-1">
                      {report.description || 'Sem descrição'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Visualization type badge */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium capitalize">
                    {report.config?.visualizationType === 'bar' && 'Gráfico de Barras'}
                    {report.config?.visualizationType === 'line' && 'Gráfico de Linhas'}
                    {report.config?.visualizationType === 'pie' && 'Gráfico de Pizza'}
                    {report.config?.visualizationType === 'table' && 'Tabela'}
                    {report.config?.visualizationType === 'cards' && 'Cards'}
                  </span>
                  {report.config?.groupBy && (
                    <>• agrupado por {
                      report.config.groupBy === 'category' && 'categoria'}
                      {report.config.groupBy === 'cost_center' && 'centro de custo'}
                      {report.config.groupBy === 'client' && 'cliente'}
                      {report.config.groupBy === 'supplier' && 'fornecedor'}
                      {report.config.groupBy === 'month' && 'mês'}
                      {report.config.groupBy === 'week' && 'semana'}
                    </>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/reports/custom/${report.id}`);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Ver
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/reports/custom/new?from=${report.id}`);
                    }}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Customizar
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/reports/custom/${report.id}/edit`);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(report.id);
                        }}
                      >
                        <Star className="h-4 w-4 mr-2" />
                        {report.is_favorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(report.id);
                        }}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteId(report.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Relatório</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </MainLayout>
  );
}
